/**
 * Configurable shutdown / cleanup controller.
 *
 * The factory form keeps process listeners and lifecycle policy isolated for
 * embedded consumers and tests. Convenience exports below preserve the simple
 * singleton API used by generated MCP servers.
 *
 * Configuration is applied in place (`reconfigure`) rather than by replacing the
 * controller, so cleanups registered before a consumer configures the singleton
 * still run. Replacing it silently dropped them.
 *
 * Diagnostics have a default sink (the package logger + a synchronous stderr
 * line for error-level events). Installing an uncaughtException listener
 * suppresses Node's own stderr report, so before the default sink existed, a
 * consumer that never wired `onDiagnostic` had crashes vanish without trace.
 */
import { error as logError, info as logInfo, writeStderrLine } from "./logger.js";
function defaultDiagnosticSink(d) {
    if (d.level === "error") {
        logError(`shutdown: ${d.event}`, d.data);
        let suffix = "";
        if (d.data !== undefined) {
            try {
                suffix = ` ${JSON.stringify(d.data)}`;
            }
            catch {
                // Diagnostic data is controller-built and serializable, but the sink
                // runs during crashes — degrade rather than throw.
            }
        }
        writeStderrLine(`[shutdown] ${d.event}${suffix}`);
    }
    else {
        logInfo(`shutdown: ${d.event}`, d.data);
    }
}
function resolveForceExitAfterMs(value) {
    const forceExitAfterMs = value ?? 3_000;
    if (!Number.isFinite(forceExitAfterMs) || forceExitAfterMs <= 0) {
        throw new Error("forceExitAfterMs must be a positive finite number");
    }
    return forceExitAfterMs;
}
export function createShutdownController(options = {}) {
    let config = { ...options };
    let hostProcess = config.process ?? process;
    let exit = config.exit ?? ((code) => hostProcess.exit(code));
    let forceExitAfterMs = resolveForceExitAfterMs(config.forceExitAfterMs);
    let orphanIntervalMs = 5_000;
    const registry = new Set();
    const listeners = [];
    let shuttingDown = false;
    let forceExitTimer = null;
    let forceExitTriggered = false;
    let orphanTimer = null;
    let handlersInstalled = false;
    let stdinListener;
    // Closure-scoped, not module-scoped: two controllers (an embedded one and the
    // singleton) must not share a cause.
    let shutdownCause = null;
    const noteShutdownCause = (cause) => {
        if (shutdownCause === null)
            shutdownCause = cause;
    };
    const diagnostic = (level, event, data) => {
        const sink = config.onDiagnostic ?? defaultDiagnosticSink;
        try {
            sink({ level, event, ...(data ? { data } : {}) });
        }
        catch {
            // A throwing sink mid-shutdown would mask the event it was reporting.
        }
    };
    const registerCleanup = (fn) => {
        registry.add(fn);
    };
    const unregisterCleanup = (fn) => {
        registry.delete(fn);
    };
    const stopOrphanTimer = () => {
        if (!orphanTimer)
            return;
        clearInterval(orphanTimer);
        orphanTimer = null;
    };
    const runCleanup = async () => {
        for (const fn of registry) {
            try {
                await fn();
            }
            catch (error) {
                diagnostic("error", "cleanup_failed", {
                    message: error instanceof Error ? error.message : String(error),
                });
            }
        }
        registry.clear();
    };
    const shutdown = async (exitCode = 0) => {
        if (shuttingDown)
            return;
        shuttingDown = true;
        stopOrphanTimer();
        forceExitTimer = setTimeout(() => {
            forceExitTriggered = true;
            diagnostic("error", "cleanup_timeout", {
                timeout_ms: forceExitAfterMs,
            });
            exit(exitCode);
        }, forceExitAfterMs);
        forceExitTimer.unref();
        await runCleanup();
        if (forceExitTimer)
            clearTimeout(forceExitTimer);
        forceExitTimer = null;
        if (!forceExitTriggered)
            exit(exitCode);
    };
    const syncCleanup = () => {
        for (const fn of registry) {
            try {
                const result = fn();
                if (result && typeof result.catch === "function")
                    result.catch(() => { });
            }
            catch {
                // Last-resort cleanup cannot recover.
            }
        }
    };
    const addListener = (event, listener) => {
        hostProcess.on(event, listener);
        listeners.push([event, listener]);
    };
    const installHandlers = () => {
        if (handlersInstalled)
            return;
        handlersInstalled = true;
        for (const signal of ["SIGINT", "SIGTERM", "SIGHUP", "SIGQUIT"]) {
            addListener(signal, ((received) => {
                noteShutdownCause(`signal:${received}`);
                diagnostic("info", "signal_received", { signal: received });
                void shutdown(received === "SIGINT" ? 130 : 0);
            }));
        }
        addListener("unhandledRejection", ((reason) => {
            diagnostic("error", "unhandled_rejection", {
                reason: reason instanceof Error ? reason.message : String(reason),
                stack: reason instanceof Error ? reason.stack : undefined,
            });
            // Only a cause when it actually initiates the shutdown. Recorded
            // unconditionally, a SURVIVED error became the permanent cause and
            // first-writer-wins then masked the real one hours later — the exact
            // inversion of the property it exists to protect. The diagnostic above is
            // the channel for "this process survived an error"; the cause is not.
            if (config.exitOnUnhandledRejection ?? true) {
                noteShutdownCause("unhandled_rejection");
                void shutdown(70);
            }
        }));
        addListener("uncaughtException", ((error) => {
            diagnostic("error", "uncaught_exception", {
                message: error.message,
                stack: error.stack,
            });
            // Gated for the same reason as unhandledRejection above — a long-running
            // TUI pins exitOnUncaughtException:false and survives these routinely.
            if (config.exitOnUncaughtException ?? true) {
                noteShutdownCause("uncaught_exception");
                void shutdown(70);
            }
        }));
        addListener("exit", syncCleanup);
    };
    const enableStdinEofDetection = () => {
        if (stdinListener)
            return;
        stdinListener = () => {
            if (shuttingDown)
                return;
            noteShutdownCause("stdin_eof");
            // Previously silent: this path and the orphan watchdog below shut the
            // process down without emitting anything, so a consumer sink observed a
            // shutdown with no cause at all.
            diagnostic("info", "stdin_eof");
            void shutdown(0);
        };
        hostProcess.stdin.on("end", stdinListener);
        hostProcess.stdin.resume();
    };
    const enableOrphanWatchdog = (intervalMs = 5_000) => {
        if (orphanTimer)
            return;
        // Recorded only once armed, so a relocation re-arms with the interval that
        // is actually in force rather than one a later no-op call requested.
        orphanIntervalMs = intervalMs;
        const parentPid = hostProcess.ppid;
        orphanTimer = setInterval(() => {
            if (hostProcess.ppid !== 1 && hostProcess.ppid === parentPid)
                return;
            noteShutdownCause("orphaned");
            diagnostic("info", "orphaned", { parent_pid: parentPid, current_ppid: hostProcess.ppid });
            void shutdown(0);
        }, intervalMs);
        orphanTimer.unref();
    };
    const dispose = () => {
        stopOrphanTimer();
        if (forceExitTimer)
            clearTimeout(forceExitTimer);
        forceExitTimer = null;
        for (const [event, listener] of listeners.splice(0)) {
            hostProcess.removeListener(event, listener);
        }
        handlersInstalled = false;
        if (stdinListener) {
            hostProcess.stdin.removeListener("end", stdinListener);
            stdinListener = undefined;
        }
    };
    const reconfigure = (next) => {
        const merged = { ...config, ...next };
        // Validate first: an invalid option must leave the controller untouched
        // rather than half-applied.
        const nextForceExitAfterMs = resolveForceExitAfterMs(merged.forceExitAfterMs);
        const nextHostProcess = merged.process ?? process;
        const relocating = nextHostProcess !== hostProcess;
        // dispose() detaches listeners and timers but deliberately leaves the
        // cleanup registry alone; discarding the whole controller is what used to
        // drop consumer state.
        const hadHandlers = handlersInstalled;
        const hadStdinDetection = Boolean(stdinListener);
        const hadOrphanWatchdog = orphanTimer !== null;
        if (relocating)
            dispose();
        config = merged;
        forceExitAfterMs = nextForceExitAfterMs;
        hostProcess = nextHostProcess;
        exit = merged.exit ?? ((code) => hostProcess.exit(code));
        if (relocating) {
            if (hadHandlers)
                installHandlers();
            if (hadStdinDetection)
                enableStdinEofDetection();
            if (hadOrphanWatchdog)
                enableOrphanWatchdog(orphanIntervalMs);
        }
        diagnostic("info", "shutdown_reconfigured", {
            force_exit_after_ms: forceExitAfterMs,
            exit_on_uncaught_exception: merged.exitOnUncaughtException ?? true,
            exit_on_unhandled_rejection: merged.exitOnUnhandledRejection ?? true,
            host_process_replaced: relocating,
        });
    };
    const reset = () => {
        dispose();
        registry.clear();
        shuttingDown = false;
        forceExitTriggered = false;
        shutdownCause = null;
    };
    return {
        registerCleanup,
        unregisterCleanup,
        shutdown,
        installHandlers,
        enableStdinEofDetection,
        enableOrphanWatchdog,
        isShuttingDown: () => shuttingDown,
        getShutdownCause: () => shutdownCause ?? "normal",
        noteShutdownCause,
        reconfigure,
        dispose,
        reset,
    };
}
let defaultController = createShutdownController();
export function registerCleanup(fn) {
    defaultController.registerCleanup(fn);
}
export function unregisterCleanup(fn) {
    defaultController.unregisterCleanup(fn);
}
export async function shutdown(exitCode = 0) {
    await defaultController.shutdown(exitCode);
    return new Promise(() => { });
}
export function installShutdownHandlers(options = {}) {
    if (Object.keys(options).length > 0)
        defaultController.reconfigure(options);
    defaultController.installHandlers();
}
export function enableStdinEofDetection() {
    defaultController.enableStdinEofDetection();
}
export function enableOrphanWatchdog(intervalMs = 5_000) {
    defaultController.enableOrphanWatchdog(intervalMs);
}
export function isShuttingDown() {
    return defaultController.isShuttingDown();
}
/** See {@link ShutdownController.getShutdownCause}. */
export function getShutdownCause() {
    return defaultController.getShutdownCause();
}
/** See {@link ShutdownController.noteShutdownCause}. First writer wins. */
export function noteShutdownCause(cause) {
    defaultController.noteShutdownCause(cause);
}
/** @internal */
export function _resetForTests() {
    defaultController.reset();
    defaultController = createShutdownController();
}
//# sourceMappingURL=shutdown.js.map